import { CandidateRating, DraftItem } from "src/app/models/models";
import { inferFormConfigVersion, mergeFormDetailsForConfigInference, withFormConfigVersion } from "./form-config-version.utils";
import { isTs1V3OrV4RatingFormVersion } from "./rating-form-version.utils";

/**
 * Assessment JSON inside `staging_assessment.assessment`.
 * May omit `id` when wrapped by a list-row DTO.
 */
type StagingAssessmentPayload = Partial<DraftItem> & {
	id?: string;
	trainingAssessmentId?: string;
	ratingFormVersion?: number;
};

/** Row returned by `GET /rating/instructor-staging-assessment`. */
export interface StagingApiListEntry {
	id?: string;
	trainingAssessmentId?: string;
	ratingFormVersion?: number;
	assessment?: StagingAssessmentPayload;
}

function hydrateFormDetails(formDetails: DraftItem["formDetails"]): DraftItem["formDetails"] {
	if (formDetails?.configVersion != null) {
		return formDetails;
	}
	return withFormConfigVersion(formDetails, inferFormConfigVersion(formDetails));
}

function resolveMergedFormDetails(serverDraft: DraftItem, localDraft: DraftItem): DraftItem["formDetails"] {
	const serverFd = serverDraft.formDetails;
	const localFd = localDraft.formDetails;
	const serverCv = serverFd?.configVersion;
	const localCv = localFd?.configVersion;

	if (serverCv != null && !Number.isNaN(Number(serverCv))) {
		return serverFd;
	}
	if (localCv != null && !Number.isNaN(Number(localCv))) {
		return { ...serverFd, configVersion: Number(localCv) };
	}

	const mergedForInfer = mergeFormDetailsForConfigInference(serverFd, localFd);
	return withFormConfigVersion(serverFd, inferFormConfigVersion(mergedForInfer));
}

function readStagingAssessmentPayload(entry: StagingApiListEntry): StagingAssessmentPayload {
	if (entry.assessment != null && typeof entry.assessment === "object") {
		return entry.assessment;
	}
	return entry as StagingAssessmentPayload;
}

function resolveStagingDraftId(entry: StagingApiListEntry, assessment: StagingAssessmentPayload): string | undefined {
	return entry.id ?? entry.trainingAssessmentId ?? assessment.id ?? assessment.trainingAssessmentId;
}

function resolveMergedRatingFormVersion(serverDraft: DraftItem, localDraft?: DraftItem): number | undefined {
	const serverVersion = serverDraft.ratingFormVersion;
	const localVersion = localDraft?.ratingFormVersion;

	if (serverVersion != null && !Number.isNaN(Number(serverVersion))) {
		return Number(serverVersion);
	}
	if (localVersion != null && !Number.isNaN(Number(localVersion))) {
		return Number(localVersion);
	}
	return undefined;
}

/**
 * Convert a staging list API row into a `DraftItem` for IndexedDB.
 */
export function normalizeStagingApiEntryToDraft(entry: StagingApiListEntry): DraftItem | null {
	const assessment = readStagingAssessmentPayload(entry);
	const id = resolveStagingDraftId(entry, assessment);
	if (!id) {
		return null;
	}

	const ratingFormVersion = entry.ratingFormVersion ?? assessment.ratingFormVersion;

	return {
		...(assessment as DraftItem),
		id,
		...(ratingFormVersion != null ? { ratingFormVersion } : {}),
	};
}

function mergeV3CandidateRatingFields(
	candidateRating: CandidateRating,
	localCandidateRating: CandidateRating | undefined,
): void {
	if (localCandidateRating?.crewRoleAssignment != null) {
		candidateRating.crewRoleAssignment = localCandidateRating.crewRoleAssignment;
	}
	if (localCandidateRating?.maneuverOutcomes != null) {
		candidateRating.maneuverOutcomes = localCandidateRating.maneuverOutcomes;
	}
	if (localCandidateRating?.temCompetencyOutcomes != null) {
		candidateRating.temCompetencyOutcomes = localCandidateRating.temCompetencyOutcomes;
	}
	if (localCandidateRating?.obCommentsEnabled != null) {
		candidateRating.obCommentsEnabled = localCandidateRating.obCommentsEnabled;
	}
}

/**
 * Merge a server staging draft with local IndexedDB state.
 * Preserves frontend-only v3 fields that may be absent from the API payload.
 */
export function mergeDraftFrontendFieldsFromLocal(serverDraft: DraftItem, localDraft?: DraftItem): DraftItem {
	const ratingFormVersion = resolveMergedRatingFormVersion(serverDraft, localDraft);

	if (!localDraft) {
		const formDetails = hydrateFormDetails(serverDraft.formDetails);
		const draftBase: DraftItem =
			formDetails === serverDraft.formDetails ? serverDraft : { ...serverDraft, formDetails };

		return {
			...draftBase,
			formDetails,
			...(ratingFormVersion != null ? { ratingFormVersion } : {}),
		};
	}

	const mergedFormDetails = resolveMergedFormDetails(serverDraft, localDraft);
	const candidateRating: CandidateRating = {
		...(serverDraft.candidateRating ?? {}),
		...(localDraft.candidateRating ?? {}),
	};

	if (isTs1V3OrV4RatingFormVersion(ratingFormVersion)) {
		mergeV3CandidateRatingFields(candidateRating, localDraft.candidateRating);
	}

	return {
		...serverDraft,
		formDetails: mergedFormDetails,
		...(ratingFormVersion != null ? { ratingFormVersion } : {}),
		candidateRating,
	};
}
